import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
    ProjectDto,
    ProjectFilterInput,
    ProjectSearchType,
    ProjectService,
    PromotedProjectFilterInput
} from "../../../api/psc";
import { defaultLimit } from "../../../constants/GlobalConstants";
import { createRandomId } from "../../../utils/random";
import { projectUpdatedAction, projectVisitedAction } from "../../../slices/SharedActions";
import { clearSelectedProjects, setProjectDetailIndex } from "../../../slices/SharedSlice";

export interface PromotedProjectState {
    id: string,
    isLoading: boolean,
    page: number,
    projects: ProjectDto[],
    totalCount: number,
    filter: PromotedProjectFilterInput,
}

const initialState: PromotedProjectState = {
    id: '',
    isLoading: true,
    page: 0,
    projects: new Array(defaultLimit).fill(null).map((_, i) => {
        return { hashId: i.toString() } as ProjectDto;
    }),
    totalCount: 0,
    filter: {
        type: ProjectSearchType.NEW,
        cityIds: [],
        regionIds: [],
        subRegionIds: [],
        phaseIds: [],
        folderIds: [],
        businessLineIds: []
    },
};

const promotedProjectSlice = createSlice({
    name: 'promoted',
    initialState,
    reducers: {
        setPromotedProjectFilter(state: PromotedProjectState, action: PayloadAction<ProjectFilterInput>) {
            const isTypeChanged = state.filter.type !== action.payload.type;

            const id = isTypeChanged ? createRandomId(5) : state.id;
            const page = isTypeChanged ? 0 : state.page;

            return {
                ...state,
                isLoading: isTypeChanged,
                filter: action.payload,
                id: id,
                page: page
            };
        },
        setPromotedProjectsPage(state: PromotedProjectState, action: PayloadAction<number>) {
            return {
                ...state,
                page: action.payload,
                isLoading: true,
                id: createRandomId(5)
            };
        },
        setPromotedProjects(state: PromotedProjectState, action: PayloadAction<{
            projects: ProjectDto[],
            totalCount: number;
        }>) {
            return {
                ...state,
                isLoading: false,
                totalCount: action.payload.totalCount,
                projects: action.payload.projects,
            };
        },
        reloadPromotedProject(state: PromotedProjectState, _action: PayloadAction) {
            return {
                ...state,
                isLoading: true,
                id: createRandomId(5)
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(projectUpdatedAction, (s: PromotedProjectState, a: PayloadAction<ProjectDto>) => {
            const project = s.projects.find(p => p.hashId === a.payload.hashId);
            const updatedProjectList = [...s.projects];

            if (project) {
                const indexOfProject = updatedProjectList.indexOf(project);
                updatedProjectList[indexOfProject] = a.payload;
            }

            return { ...s, projects: updatedProjectList };
        });
        builder.addCase(projectVisitedAction, (s: PromotedProjectState, a: PayloadAction<string>) => {
            const project = s.projects.find(p => p.hashId === a.payload);
            const updatedProjectList = [...s.projects];

            if (project) {
                const index = updatedProjectList.indexOf(project);
                updatedProjectList[index] = { ...project, isVisited: true };
            }

            return {
                ...s,
                projects: updatedProjectList
            };
        });
    }

});

export async function FetchPromotedProjects(state: PromotedProjectState, dispatch: Dispatch) {
    const projects = await ProjectService.filterPromotedProjects('1', defaultLimit, state.page * defaultLimit, state.filter);

    dispatch(setPromotedProjects({
        projects: projects.results,
        totalCount: projects.count,
    }));

    dispatch(clearSelectedProjects());
    dispatch(setProjectDetailIndex(0));
}


export const {
    setPromotedProjectFilter,
    setPromotedProjects,
    setPromotedProjectsPage,
    reloadPromotedProject,
} = promotedProjectSlice.actions;
export default promotedProjectSlice.reducer

